/**
 * The sync engine (WS1, budgets WS2): after each call it drains the executor's dirty paths and
 * sends them to the server as deltas on the host session, so the workspace survives the tab.
 *
 * A round (a call's save, a debounced one, or a flush) takes every path marked since the last
 * one and sends, in order:
 * - deletes first, since a delete never stands in the way of a later put;
 * - then the files by sync class: work and uploads, then publish. data/ holds exports, which the
 *   server keeps as provenance only: their puts are not sent (it refuses them), their deletes are;
 * - a put whose bytes the server already holds at that path (the restore index, kept up to date
 *   from every change applied, the stage op's too) is skipped.
 * Batches hold at most 100 ops (the server's limit) and a few MB of gzip.
 *
 * Budgets: a file over 10 MB is sent only on a good link (link.ts). Until then it goes as
 * `not_kept {path, size}`, so the server indexes it as gone rather than restore a stale copy, and
 * is held until the link allows. One round sends at most 64 MB (its first file always goes,
 * however big), a flush any amount; files past that stay queued with no op (the server keeps its
 * last copy). A call's save sends that call's own changes first (after every delete) and whole,
 * whatever their size, so a rename is never half applied by the budget; it holds back only the
 * left-over work behind them.
 *
 * Failures: a request that failed on the way (network, 5xx) is sent again, backing off, a few
 * times; then the changes stay queued, and the server's sha of each is marked unsure (it may or
 * may not have applied them), so their next round sends them whatever the server held. A lost lease stops the engine for good and tells the host. A server
 * that takes no changes from this host now (401, 403, 404) ends the round; the changes wait.
 * Changes the server refuses as they are (400, 422, a path conflict) are sent again in halves
 * until the ones it refuses on their own are found; only those are dropped (logged as a count and
 * the server's code), and their shas are not recorded, so their next change sends them. A full
 * workspace is split the same way; the puts that do not fit on their own go as not kept, together
 * at the end of the round, and are not uploaded again until the file changes, a delete of ours is
 * applied, or a flush tries them once.
 *
 * A call's answer waits for its changes (`saveCall`): the host hands it in only once the server
 * holds them, so a reload right after finds them. They are what the call's own drain found and
 * what rounds drained during the call (a debounced or retry round may take a write while the call
 * runs), not what was dirty when it began (`beforeCall`: an upload pickup's writes). The save goes
 * at once, behind a round in flight (again while a round left fewer of them queued), and backs off
 * between tries as any round does; the host bounds the wait by the call's deadline. A call that
 * changed nothing sends and waits for nothing.
 *
 * Work a round leaves queued (files past the budget, unreadable ones, a failed send) gets another
 * round on its own: soon after a round that sent something, backing off up to 30 s after a failed
 * one, and no more after a few rounds in a row that sent nothing, until the next call, flush or
 * link change. Big files held for the link go when it turns good, or with the next call or flush.
 * A flush cuts every backoff short.
 */
import { sha256Hex } from "../core/bytes";
import { SandboxError } from "../core/errors";
import { nameProblem, workspaceRelative } from "../core/paths";
import type { DirtyPath, Executor } from "../core/types";
import { LeaseError, SyncDenied, SyncRefused } from "./api";
import { browserLinkIsGood, onBrowserLinkChange } from "./link";
import { prepareInline, type Prepare, type Prepared } from "./prepare";
import type { DeltaChange, HostSession } from "./session";

const MB = 1024 * 1024;

export const SYNC_LIMITS = {
  debounceMs: 500,
  /** Over this a file needs a good link. */
  bigFileBytes: 10 * MB,
  /** What one round after a call sends at most (raw bytes). */
  roundBytes: 64 * MB,
  /** backend sync.BATCH_MAX_OPS. */
  batchOps: 100,
  /** gzip bytes per batch, so a batch stays one modest request. */
  batchBytes: 8 * MB,
  tries: 6,
  /** Backoff between tries of a batch, and before another round for left-over work. */
  retryBaseMs: 1_000,
  retryMaxMs: 30_000,
};

export type SyncLimits = typeof SYNC_LIMITS;

/** Send order by sync class (deletes go before all of them). */
const CLASS_ORDER: Record<DirtyPath["syncClass"], number> = {
  work: 0,
  uploads: 0,
  publish: 1,
  data: 2,
};

/** The running executor's files, with what the server stored at its revival. */
export type SyncSource = Pick<Executor, "drainDirty" | "exportFile"> & {
  /** sha256 of each stored file by workspace path (the restore index). */
  synced?: Record<string, string>;
};

export interface SyncEngineDeps {
  session: HostSession;
  /** The workspace's executor; null until its first call starts it. */
  source(): SyncSource | null;
  /** This tab no longer holds the workspace: the engine has stopped. */
  onLeaseLost(): void;
  timers: {
    setTimeout(callback: () => void, ms: number): unknown;
    clearTimeout(handle: unknown): void;
  };
  prepare?: Prepare;
  linkIsGood?: () => boolean;
  /** Calls the listener when the link may have changed; returns the unsubscribe. */
  onLinkChange?: (listener: () => void) => () => void;
  limits?: Partial<SyncLimits>;
  log?: Pick<Console, "warn" | "error">;
}

export interface SyncEngine {
  /**
   * A call starts: what is dirty now (an upload pickup's writes, between calls) is queued as no
   * call's, so the call's save neither waits on it nor answers UNSAVED for it.
   */
  beforeCall(): Promise<void>;
  /** A call finished that nobody waits on (dropped): sync it once calls pause for the debounce. */
  afterCall(): void;
  /**
   * A call finished: send what it changed now and resolve once the server holds it, so the call's
   * answer can go out. Its changes are all drained since the last save, whichever round drained
   * them. A call that changed nothing waits for nothing (left-over work keeps its own rounds).
   * Rejects with the failure when a request for its changes failed, with SyncStopped when the
   * engine stopped, and with SyncIncomplete when some of them did not get there: unreadable,
   * refused, sent as not kept (too big for the link or for the workspace's room), or still queued
   * once a round leaves as many of them queued as the round before.
   */
  saveCall(): Promise<void>;
  /**
   * Sends everything changed now (release, handover). Rejects when the engine has stopped (a lost
   * lease, the host let go), when a request failed, and when anything was left unsent or dropped:
   * a file it could not read or prepare, a change the server refused. A big file sent as not kept
   * (the link, a full workspace) counts as handled.
   */
  flush(): Promise<void>;
  /** Changes another sender (the stage op) had applied: the server holds them now. */
  applied(changes: DeltaChange[]): void;
  /** Changes another sender's failed batch may or may not have applied. */
  unsure(changes: DeltaChange[]): void;
  /**
   * What the server holds at a workspace path as far as this engine knows (the restore index and
   * every change applied since): a sha256, a not-kept mark, an unsure mark (a change of ours it
   * may or may not have applied), or undefined when nothing. A live file that differs from it has
   * a change not on the server yet (WS3's upload pickup).
   */
  serverHolds(path: string): string | undefined;
  /** Sets what the server holds at a path (`serverHolds`'s value; undefined for nothing). */
  recordHolds(path: string, holds: string | undefined): void;
  stop(): void;
}

/** The engine stopped: its lease was lost, or the host let the workspace go. */
export class SyncStopped extends Error {}

/** A flush that could not send everything. */
export class SyncIncomplete extends Error {
  constructor(readonly unsent: number) {
    super(`sandbox sync: ${unsent} changes could not be sent`);
  }
}

/** A change to send, and the dirty path it settles. */
interface Planned {
  dirty: DirtyPath;
  change: DeltaChange;
  /** Raw bytes a put carries. */
  bytes: number;
}

/** What one round did, counted as it goes. */
interface RoundState {
  /** Raw bytes it may still send once it took a put. */
  budget: number;
  puts: number;
  /** Changes the server applied. */
  sent: number;
  /** Files past the budget, left queued. */
  later: number;
  /** Files it could not read or prepare (left queued) and changes dropped. */
  unsent: number;
  /** Changes the server refused on their own, and its codes for them. */
  refused: number;
  reasons: Set<string>;
  /** Not-kept changes for puts a full workspace had no room for, sent at the end of the round. */
  notKept: Planned[];
  /** A call's save's changes: sent first, and never left for the budget (a rename goes whole). */
  own: Set<DirtyPath> | null;
}

export function createSyncEngine(deps: SyncEngineDeps): SyncEngine {
  const limits = { ...SYNC_LIMITS, ...deps.limits };
  const prepare = deps.prepare ?? prepareInline;
  const linkIsGood = deps.linkIsGood ?? browserLinkIsGood;
  const log = deps.log ?? console;
  const { session, timers } = deps;

  /** Drained, not on the server yet; by absolute path, in the order of their latest change. */
  const queued = new Map<string, DirtyPath>();
  /** Big files sent as not kept until the link allows. */
  const held = new Map<string, DirtyPath>();
  /** Puts that did not fit a full workspace, sent as not kept, with the sha that did not fit. */
  const full = new Map<string, { dirty: DirtyPath; sha256: string }>();
  /** What the server holds by workspace path: a sha256, or `notKeptMark`. */
  let known: Map<string, string> | null = null;
  /** Changes the server will not hold as they are (refused, not kept): a call's are not saved. */
  const unsaved = new WeakSet<DirtyPath>();
  /** Drained since the call began: that call's changes, whichever round drained them. */
  const drained: DirtyPath[] = [];
  /** What happened to the index before there was an executor to read it from, replayed then. */
  const early: (() => void)[] = [];
  /** A delete of ours was applied since the full puts were last tried. */
  let freed = false;
  let stopped = false;
  let debounce: unknown = null;
  let again: unknown = null;
  let backoffs = 0;
  /** Rounds in a row that left work queued and sent nothing. */
  let idle = 0;
  /** A flush is waiting: backoffs end at once. */
  let flushing = false;
  let sleeping: (() => void) | null = null;
  let rounds: Promise<unknown> = Promise.resolve();
  let lastRound: RoundState | null = null;
  const unsubscribe = (deps.onLinkChange ?? onBrowserLinkChange)(() => {
    if (!held.size || !linkIsGood()) return;
    idle = 0;
    schedule(limits.debounceMs);
  });

  const backoff = (n: number) => Math.min(limits.retryBaseMs * 2 ** n, limits.retryMaxMs);

  /**
   * Sleeps `ms`; flush(), saveCall() and stop() wake it early, and it does not sleep while a flush
   * waits.
   */
  const wait = (ms: number) =>
    new Promise<void>((resolve) => {
      if (flushing || stopped) return resolve();
      const timer = timers.setTimeout(() => wake(), ms);
      sleeping = () => {
        timers.clearTimeout(timer);
        sleeping = null;
        resolve();
      };
    });
  const wake = () => sleeping?.();

  function index(source: SyncSource): Map<string, string> {
    if (!known) {
      known = new Map(Object.entries(source.synced ?? {}));
      for (const replay of early.splice(0)) replay();
    }
    return known;
  }

  /** Records changes the server applied. */
  function remember(changes: DeltaChange[]) {
    if (!known) {
      early.push(() => remember(changes));
      return;
    }
    for (const change of changes) {
      if (change.op === "put") known.set(change.path, change.sha256);
      else if (change.op === "not_kept") known.set(change.path, notKeptMark(change.size));
      else {
        known.delete(change.path);
        freed = true;
      }
    }
  }

  /**
   * Marks what the server holds at these paths unsure, so their next change is always sent. Not
   * forgotten: an upload pickup must not read a delete of ours the server may not have as nothing.
   */
  function forget(changes: DeltaChange[]) {
    if (!known) {
      early.push(() => forget(changes));
      return;
    }
    for (const change of changes) known.set(change.path, UNSURE);
  }

  function recordHolds(path: string, holds: string | undefined) {
    if (!known) {
      early.push(() => recordHolds(path, holds));
      return;
    }
    if (holds === undefined) known.delete(path);
    else known.set(path, holds);
  }

  function round(capped: boolean, own: Set<DirtyPath> | null = null): Promise<number> {
    const ran = rounds.then(() => run(capped, own));
    rounds = ran.then(
      () => settled(false),
      (error: unknown) => settled(!(error instanceof SyncStopped))
    );
    return ran;
  }

  /**
   * After a round: another one while work is left queued. Soon when the round sent something,
   * backing off when it failed, and none after `tries` rounds in a row that sent nothing.
   */
  function settled(failed: boolean) {
    const sent = lastRound?.sent ?? 0;
    lastRound = null;
    if (stopped) return;
    if (!failed) backoffs = 0;
    if (!queued.size) {
      idle = 0;
      return;
    }
    idle = sent ? 0 : idle + 1;
    if (idle >= limits.tries) {
      if (idle === limits.tries) {
        log.warn(
          "sandbox sync: changes left after rounds that sent nothing; waiting for the next call"
        );
      }
      return;
    }
    const ms = failed ? backoff(backoffs++) : sent ? limits.debounceMs : limits.retryBaseMs;
    if (debounce === null && again === null) schedule(ms, true);
  }

  function schedule(ms: number, retry = false) {
    if (stopped) return;
    const timer = timers.setTimeout(() => {
      if (retry) again = null;
      else debounce = null;
      round(true).catch((error: unknown) => {
        if (!(error instanceof SyncStopped))
          log.warn("sandbox sync: a round failed, kept for the next", error);
      });
    }, ms);
    if (retry) again = timer;
    else {
      timers.clearTimeout(debounce);
      debounce = timer;
    }
  }

  /** Whether the server holds a file's bytes as they are now (a full sha, not unsure or not kept). */
  async function holdsAlready(source: SyncSource, dirty: DirtyPath, path: string) {
    const holds = index(source).get(path);
    if (!holds || holds === UNSURE || holds.startsWith("not_kept:")) return false;
    try {
      return (await sha256Hex(await source.exportFile(dirty.path))) === holds;
    } catch {
      return false;
    }
  }

  /**
   * Settles a queued change, unless a newer one for its path came while it was out (a round runs
   * while a call's save drains): that one stays queued.
   */
  function dequeue(dirty: DirtyPath) {
    if (queued.get(dirty.path) === dirty) queued.delete(dirty.path);
  }

  /** Queues a drained change (last in the order of changes), as the current call's. */
  function enqueue(dirty: DirtyPath) {
    queued.delete(dirty.path);
    queued.set(dirty.path, dirty);
    drained.push(dirty);
  }

  function cancelTimers() {
    timers.clearTimeout(debounce);
    timers.clearTimeout(again);
    debounce = again = null;
  }

  /** One round; resolves with the number of changes it left unsent or dropped. */
  async function run(capped: boolean, own: Set<DirtyPath> | null): Promise<number> {
    if (!capped) flushing = false;
    if (stopped) throw new SyncStopped("sandbox sync stopped");
    const source = deps.source();
    if (!source) return 0;
    index(source);
    const state: RoundState = {
      budget: capped ? limits.roundBytes : Infinity,
      puts: 0,
      sent: 0,
      later: 0,
      unsent: 0,
      refused: 0,
      reasons: new Set(),
      notKept: [],
      own,
    };
    lastRound = state;
    for (const dirty of await source.drainDirty()) enqueue(dirty);
    for (const [path, dirty] of held) if (!queued.has(path)) queued.set(path, dirty);
    held.clear();
    if (!capped || freed) {
      for (const [path, { dirty }] of full) if (!queued.has(path)) queued.set(path, dirty);
      full.clear();
      freed = false;
    }

    let batch: Planned[] = [];
    let batchBytes = 0;
    for (const dirty of sendOrder([...queued.values()], own)) {
      const planned = await plan(source, dirty, state);
      if (!planned) continue;
      if (planned.change.op === "put") state.puts++;
      state.budget -= planned.bytes;
      batch.push(planned);
      batchBytes += planned.change.op === "put" ? planned.change.body.length : 0;
      if (batch.length >= limits.batchOps || batchBytes >= limits.batchBytes) {
        await deliver(batch, state);
        batch = [];
        batchBytes = 0;
      }
    }
    if (batch.length) await deliver(batch, state);
    const notKept = state.notKept.splice(0);
    for (let start = 0; start < notKept.length; start += limits.batchOps) {
      await deliver(notKept.slice(start, start + limits.batchOps), state);
    }
    if (state.later) {
      log.warn(
        `sandbox sync: ${state.later} files past this call's budget, left for the next round`
      );
    }
    if (state.refused) {
      log.error(
        `sandbox sync: the server refused ${state.refused} changes (${[...state.reasons].join(", ")}); dropped until they change again`
      );
    }
    return state.unsent + state.later;
  }

  /**
   * The change for one dirty path, or null when there is nothing to send now: settled, left for
   * a round with budget (`later`), or not readable or preparable (`unsent`, left queued).
   */
  async function plan(
    source: SyncSource,
    dirty: DirtyPath,
    state: RoundState
  ): Promise<Planned | null> {
    const path = workspaceRelative(dirty.path);
    const problem = nameProblem(dirty.path);
    if (path === null || problem) {
      log.warn("sandbox sync: a file with a name the server refuses is not synced");
      dequeue(dirty);
      unsaved.add(dirty);
      state.unsent++;
      return null;
    }
    if (dirty.op === "delete") return { dirty, change: { op: "delete", path }, bytes: 0 };
    if (dirty.syncClass === "data") {
      dequeue(dirty); // an export's bytes are made again from its provenance
      return null;
    }
    const size = dirty.size ?? 0;
    if (size > limits.bigFileBytes && !linkIsGood()) {
      if (await holdsAlready(source, dirty, path)) {
        dequeue(dirty); // a pickup's upload, say: the server has these bytes, nothing to send
        return null;
      }
      held.set(dirty.path, dirty);
      dequeue(dirty);
      unsaved.add(dirty);
      if (index(source).get(path) === notKeptMark(size)) return null; // the server knows already
      log.warn(`sandbox sync: a ${size}-byte file is not kept for now: too big for this link`);
      return { dirty, change: { op: "not_kept", path, size }, bytes: 0 };
    }
    if (state.puts && size > state.budget && !state.own?.has(dirty)) {
      state.later++;
      return null;
    }
    let bytes: Uint8Array;
    try {
      bytes = await source.exportFile(dirty.path);
    } catch (error) {
      if (error instanceof SandboxError && error.code === "ENOENT") {
        return { dirty, change: { op: "delete", path }, bytes: 0 }; // gone since it was marked
      }
      log.warn("sandbox sync: could not read a file, kept for the next round", error);
      state.unsent++;
      return null;
    }
    const notFitSha = full.get(dirty.path)?.sha256;
    let prepared: Prepared;
    try {
      prepared = await prepare(bytes, notFitSha ?? index(source).get(path));
    } catch (error) {
      log.warn("sandbox sync: could not prepare a file, kept for the next round", error);
      state.unsent++;
      return null;
    }
    const { sha256, body } = prepared;
    if (!body && notFitSha && index(source).get(path) !== notKeptMark(bytes.length)) {
      // the put that did not fit, whose not_kept never went (its round ended first): send it now
      state.notKept.push({ dirty, change: { op: "not_kept", path, size: bytes.length }, bytes: 0 });
      return null;
    }
    if (!body) {
      dequeue(dirty); // the server holds it, or its not_kept
      return null;
    }
    full.delete(dirty.path);
    const mtime = Date.parse(dirty.mtime);
    const change: DeltaChange = { op: "put", path, sha256, body, ...(mtime > 0 && { mtime }) };
    return { dirty, change, bytes: bytes.length };
  }

  /**
   * Sends a batch; a batch the server refuses, or that does not fit, is sent again in halves. A
   * change refused on its own is dropped; a put that does not fit on its own is held for room
   * and its not_kept waits for the round's not-kept batches.
   */
  async function deliver(batch: Planned[], state: RoundState): Promise<void> {
    try {
      await send(batch, state);
    } catch (error) {
      const tooBig = error instanceof SandboxError && error.code === "ENOSPC";
      if (!tooBig && !(error instanceof SyncRefused)) throw error;
      if (batch.length > 1) {
        const half = Math.ceil(batch.length / 2);
        await deliver(batch.slice(0, half), state);
        await deliver(batch.slice(half), state);
        return;
      }
      const [planned] = batch;
      if (tooBig && planned.change.op === "put") return holdForRoom(planned, state);
      state.unsent++;
      state.refused++;
      state.reasons.add(error instanceof SyncRefused ? error.reason : "workspace_full");
      unsaved.add(planned.dirty);
      dequeue(planned.dirty);
    }
  }

  /** A put the full workspace has no room for: not kept, and not uploaded again for now. */
  function holdForRoom(planned: Planned, state: RoundState) {
    if (planned.change.op !== "put") return;
    const { path, sha256 } = planned.change;
    log.warn(`sandbox sync: the workspace is full; a ${planned.bytes}-byte file is not kept`);
    full.set(planned.dirty.path, { dirty: planned.dirty, sha256 });
    unsaved.add(planned.dirty);
    freed = false; // what our deletes freed so far was not room enough
    if (known?.get(path) === notKeptMark(planned.bytes)) {
      dequeue(planned.dirty);
      return;
    }
    state.notKept.push({
      ...planned,
      change: { op: "not_kept", path, size: planned.bytes },
      bytes: 0,
    });
  }

  /** One batch, tried again while the failure is on the way; settles its paths once applied. */
  async function send(batch: Planned[], state: RoundState): Promise<void> {
    const changes = batch.map((planned) => planned.change);
    for (let tries = 1; ; tries++) {
      if (stopped) throw new SyncStopped("sandbox sync stopped");
      try {
        await session.send(changes);
        state.sent += changes.length;
        remember(changes);
        for (const { dirty } of batch) dequeue(dirty);
        return;
      } catch (error) {
        if (error instanceof LeaseError) {
          stop();
          deps.onLeaseLost();
          throw new SyncStopped(error.message);
        }
        if (
          error instanceof SyncRefused ||
          (error instanceof SandboxError && error.code === "ENOSPC")
        ) {
          throw error;
        }
        if (error instanceof SyncDenied) {
          log.warn(
            `sandbox sync: the server took no changes (HTTP ${error.status}); kept for later`
          );
          throw error;
        }
        if (tries >= limits.tries) {
          forget(changes); // the server may have applied them without its answer arriving
          throw error;
        }
        await wait(backoff(tries - 1));
      }
    }
  }

  function stop() {
    stopped = true;
    cancelTimers();
    unsubscribe();
    wake();
  }

  function afterCall() {
    drained.length = 0; // a call boundary: what is drained from now on is the next call's
    if (stopped) return;
    timers.clearTimeout(again);
    again = null;
    idle = 0;
    schedule(limits.debounceMs);
  }

  return {
    async beforeCall() {
      const source = deps.source();
      try {
        for (const dirty of source ? await source.drainDirty() : []) enqueue(dirty);
      } catch {
        // the executor could not say: the call's own drain takes them
      }
      drained.length = 0;
    },
    afterCall,
    async saveCall() {
      if (stopped) throw new SyncStopped("sandbox sync stopped");
      const source = deps.source();
      for (const dirty of source ? await source.drainDirty() : []) enqueue(dirty);
      const latest = new Map<string, DirtyPath>();
      for (const dirty of drained.splice(0)) latest.set(dirty.path, dirty);
      const changed = [...latest.values()];
      if (!changed.length) {
        afterCall(); // left-over work, if any, as before
        return;
      }
      const pending = () => changed.filter((dirty) => queued.get(dirty.path) === dirty).length;
      cancelTimers();
      idle = 0;
      wake(); // a round backing off sends again now
      let failure: unknown = null;
      try {
        // Again while a round left fewer of them queued (one in flight may have carried some).
        for (let before = pending(), now = before; now; before = now) {
          await round(true, new Set(changed));
          now = pending();
          if (now >= before) break;
        }
      } catch (error) {
        failure = error;
      }
      const left = changed.filter(
        (dirty) => queued.get(dirty.path) === dirty || unsaved.has(dirty)
      ).length;
      if (left) throw failure ?? new SyncIncomplete(left);
    },
    async flush() {
      cancelTimers();
      idle = 0;
      flushing = true;
      wake(); // a round backing off sends again now, and does not sleep again before this flush
      const unsent = await round(false);
      if (unsent) throw new SyncIncomplete(unsent);
    },
    applied: remember,
    unsure: forget,
    serverHolds(path) {
      const source = deps.source();
      return source ? index(source).get(path) : undefined;
    },
    recordHolds,
    stop,
  };
}

/**
 * Deletes first (a delete never stands in the way of a later put), then a call's own puts, then the
 * rest by class, each in the order of its latest change (the sort is stable).
 */
function sendOrder(paths: DirtyPath[], own: Set<DirtyPath> | null): DirtyPath[] {
  const rank = (dirty: DirtyPath) =>
    dirty.op === "delete" ? 0 : own?.has(dirty) ? 1 : 2 + CLASS_ORDER[dirty.syncClass];
  return paths.sort((a, b) => rank(a) - rank(b));
}

/** What `known` holds for a path the server indexes as not kept: never a sha256. */
const notKeptMark = (size: number) => `not_kept:${size}`;

/** What `known` holds for a path whose last change the server may or may not have applied. */
const UNSURE = "unsure";

/**
 * `session` synced by `engine`: its flush sends through the engine (ER4's release reaches it
 * there), and what other senders (the stage op) send is recorded in the engine's index. Errors
 * pass through unchanged.
 */
export function withSync(session: HostSession, engine: SyncEngine): HostSession {
  return {
    ...session,
    async send(changes) {
      try {
        const result = await session.send(changes);
        engine.applied(changes);
        return result;
      } catch (error) {
        engine.unsure(changes);
        throw error;
      }
    },
    flush: () => engine.flush(),
  };
}
